import React, { PropsWithChildren } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  SvgIcon,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { AngleField, DistanceField, NumericField } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  DivisionLine,
  ExactLine,
  ParallelLine,
  PerpendicularLine,
  PointSlopeLine,
  PointToPointLine,
} from '../../../../../../resources/LineTypeIcons';
import { invertDirection } from '../../../../../library/layout-geometry/directions';
import {
  DivisionInsertionWorkLine,
  ExactInsertionWorkLine,
  InsertionWorkLine,
  ParallelInsertionWorkLine,
  PerpendicularInsertionWorkLine,
  PointAngleInsertionWorkLine,
  PointToPointInsertionWorkLine,
  tryActualizeWorkLines,
} from '../../../../../library/layout-geometry/work-lines';
import { insertReviewedWorkLine } from '../../../../../store/actions/layout/workLineActions';
import { getWorkLineInsertionInfo } from '../../../../../store/selectors/workLineInsertionSelectors';
import {
  resetWorkLineGesture,
  setInsertionWorkLine,
  setInsertionWorkLineTypeManually,
  setWorkLineInsertionScope,
  setWorkLineInsertionSettings,
  WorkLineInsertionSelection,
} from '../../../../../store/slices/applicationSlice';
import { DirectionButtons } from '../../../../controls/DirectionButtons';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { WorkLineScopeSelect } from '../../properties/WorkLineScopeSelect';
import { InsertAutomaticallySwitch } from '../InsertAutomaticallySwitch';
import InsertionPanelContainer from '../InsertionPanelContainer';
import { LayoutLinePickerField } from './LayoutLinePickerField';
import { LayoutPointPickerField } from './LayoutPointPickerField';

function firstSelectionLabel(firstSelection: WorkLineInsertionSelection): string {
  switch (firstSelection.type) {
    case 'line':
      return 'Line';

    case 'point':
      return 'Point';

    default:
      assertExhaustiveSwitch(firstSelection);
  }
}

function LineTypeExplanation(props: PropsWithChildren<{ icon: typeof SvgIcon }>) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', textWrap: 'wrap' }}>
      <SvgIcon sx={{ m: 1 }} component={props.icon} inheritViewBox />
      <Typography variant="body2">{props.children}</Typography>
    </Box>
  );
}

export function WorkLineInsertionSubPanel() {
  const dispatch = useAppDispatch();
  const insertionInfo = useAppSelector(getWorkLineInsertionInfo);

  if (insertionInfo == null) return null;

  const { settings, draft, scope } = insertionInfo;
  const firstSelection = draft.phase === 'gesturing' ? draft.firstSelection : undefined;
  const reviewingLine = draft.phase === 'reviewing' ? draft.line : null;

  const disabled = reviewingLine == null || tryActualizeWorkLines(reviewingLine).length === 0;

  const hasGesture = draft.phase !== 'idle';

  // segment mode can be enabled before a gesture begins, to enable segment creation in "Insert Automatically" mode
  const segmentModeCanBeEnabled = reviewingLine == null || reviewingLine.type === 'point-to-point';

  return (
    <InsertionPanelContainer
      disabled={disabled}
      onInsert={() => {
        dispatch(insertReviewedWorkLine());
      }}
      additionalButtons={
        draft.phase !== 'reviewing' ? (
          <InsertAutomaticallySwitch
            checked={settings.autoInsert}
            canInsert={!disabled}
            insertedObjectName="work line"
            onChange={(checked) => {
              dispatch(setWorkLineInsertionSettings({ autoInsert: checked }));
            }}
            onInsert={async () => {
              await dispatch(insertReviewedWorkLine());
            }}
          />
        ) : undefined
      }
    >
      <Typography align="center" variant="h5" sx={{ marginBottom: 3 }}>
        Work Line Creation
      </Typography>

      <Stack direction="column" spacing={2}>
        <WorkLineTypeToggle
          value={reviewingLine?.type ?? null}
          onChange={(type) => {
            dispatch(setInsertionWorkLineTypeManually(type));
          }}
        />

        {reviewingLine == null ? (
          <WorkLineGestureGuidance firstSelection={firstSelection} />
        ) : (
          <WorkLineDefinitionControls line={reviewingLine} />
        )}

        <Button
          variant="outlined"
          disabled={!hasGesture}
          onClick={() => {
            dispatch(resetWorkLineGesture());
          }}
        >
          Reset
        </Button>

        <Divider />

        <WorkLineScopeSelect value={scope} onChange={(scope) => dispatch(setWorkLineInsertionScope(scope))} />

        <Tooltip disableInteractive title="Calculated truss envelopes will be bounded by this work line">
          <FormControlLabel
            label="Bounding"
            control={
              <Checkbox
                checked={settings.bounding}
                onChange={(_, checked) => dispatch(setWorkLineInsertionSettings({ bounding: checked }))}
              />
            }
          />
        </Tooltip>

        <Tooltip
          disableInteractive
          title={
            segmentModeCanBeEnabled
              ? 'Point-to-point lines will not extend past the chosen base and end points'
              : 'Only point-to-point work lines can be segments'
          }
        >
          <FormControlLabel
            label="Segment"
            control={
              <Checkbox
                checked={settings.segmentDesired && segmentModeCanBeEnabled}
                disabled={!segmentModeCanBeEnabled}
                onChange={(_, checked) => dispatch(setWorkLineInsertionSettings({ segmentDesired: checked }))}
              />
            }
          />
        </Tooltip>
      </Stack>
    </InsertionPanelContainer>
  );
}

function WorkLineTypeToggle(props: {
  value: InsertionWorkLine['type'] | null;
  onChange: (value: InsertionWorkLine['type']) => void;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      size="small"
      value={props.value}
      onChange={(_, clickedValue: InsertionWorkLine['type'] | null) => {
        if (clickedValue != null && clickedValue !== props.value) props.onChange(clickedValue);
      }}
    >
      <ToggleButton value="point-to-point">
        <Tooltip disableInteractive title="Point to Point">
          <PointToPointLine fontSize="small" />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="point-angle">
        <Tooltip disableInteractive title="Point + Angle">
          <PointSlopeLine fontSize="small" />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="parallel">
        <Tooltip disableInteractive title="Parallel">
          <ParallelLine fontSize="small" />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="perpendicular">
        <Tooltip disableInteractive title="Perpendicular">
          <PerpendicularLine fontSize="small" />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="division">
        <Tooltip disableInteractive title="Division">
          <DivisionLine fontSize="small" />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="exact">
        <Tooltip disableInteractive title="Exact">
          <ExactLine fontSize="small" />
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

function WorkLineGestureGuidance(props: { firstSelection: WorkLineInsertionSelection | undefined }) {
  return (
    <>
      <Typography variant="body2">Pick a type above to create a line manually, or click in the scene:</Typography>

      <LineTypeExplanation icon={PointToPointLine}>
        Point-to-point work lines are created by clicking two points.
      </LineTypeExplanation>

      <LineTypeExplanation icon={PointSlopeLine}>
        Point + angle work lines are created by clicking a point, then clicking to set the angle.
      </LineTypeExplanation>

      <LineTypeExplanation icon={ParallelLine}>
        Parallel work lines are created by clicking a line, then clicking to set the offset.
      </LineTypeExplanation>

      <LineTypeExplanation icon={PerpendicularLine}>
        Perpendicular work lines are created by clicking a line, then a point to pass through.
      </LineTypeExplanation>

      <LineTypeExplanation icon={DivisionLine}>
        Division work lines are created by clicking a line, then a parallel line — the result is one or more evenly
        spaced lines between them.
      </LineTypeExplanation>

      <LineTypeExplanation icon={ExactLine}>
        Exact work lines are created by clicking the same segment twice — the result is a line exactly on top of it.
      </LineTypeExplanation>

      <Divider />

      <Typography variant="body2" align="center" color="text.secondary">
        {props.firstSelection == null
          ? 'Waiting for your first click…'
          : `${firstSelectionLabel(props.firstSelection)} selected, waiting for a second click.`}
      </Typography>
    </>
  );
}

function WorkLineDefinitionControls(props: { line: InsertionWorkLine }) {
  switch (props.line.type) {
    case 'parallel':
      return <ParallelWorkLineControls value={props.line} />;

    case 'point-to-point':
      return <PointToPointWorkLineControls value={props.line} />;

    case 'point-angle':
      return <PointAngleWorkLineControls value={props.line} />;

    case 'perpendicular':
      return <PerpendicularWorkLineControls value={props.line} />;

    case 'division':
      return <DivisionWorkLineControls value={props.line} />;

    case 'exact':
      return <ExactWorkLineControls value={props.line} />;

    default:
      assertExhaustiveSwitch(props.line);
  }
}

function ExactWorkLineControls(props: { value: ExactInsertionWorkLine }) {
  return <LayoutLinePickerField label="Line" target="insertion-work-line-exact-base-line" value={props.value.base} />;
}

function ParallelWorkLineControls(props: { value: ParallelInsertionWorkLine }) {
  const dispatch = useAppDispatch();
  const { value } = props;

  return (
    <>
      <LayoutLinePickerField label="Base Line" target="insertion-work-line-parallel-base-line" value={value.base} />

      <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
        <DistanceField
          label="Offset"
          value={value.offset ?? 0}
          onSubmit={(offset) => {
            const updatedValue = { ...value, offset };

            if (offset < 0) {
              updatedValue.offset = offset * -1;
              if (updatedValue.direction) updatedValue.direction = invertDirection(updatedValue.direction);
            }

            dispatch(setInsertionWorkLine(updatedValue));
          }}
        />

        <DirectionButtons
          automaticMode
          value={value.direction ?? null}
          onChange={(_, direction) => {
            dispatch(setInsertionWorkLine({ ...value, direction: direction ?? undefined }));
          }}
        />
      </Stack>
    </>
  );
}

function PointAngleWorkLineControls(props: { value: PointAngleInsertionWorkLine }) {
  const dispatch = useAppDispatch();
  const { value } = props;

  return (
    <>
      <LayoutPointPickerField
        label="Base Point"
        target="insertion-work-line-point-angle-base"
        value={value.basePoint}
      />

      <AngleField
        label="Angle"
        value={value.angle ?? 0}
        onSubmit={(angle) => {
          dispatch(setInsertionWorkLine({ ...value, angle }));
        }}
      />
    </>
  );
}

function PointToPointWorkLineControls(props: { value: PointToPointInsertionWorkLine }) {
  return (
    <>
      <LayoutPointPickerField
        label="Base Point"
        target="insertion-work-line-point-to-point-base"
        value={props.value.basePoint}
      />

      <LayoutPointPickerField
        label="End Point"
        target="insertion-work-line-point-to-point-end"
        value={props.value.endPoint}
      />
    </>
  );
}

function PerpendicularWorkLineControls(props: { value: PerpendicularInsertionWorkLine }) {
  return (
    <>
      <LayoutLinePickerField
        label="Perpendicular To"
        target="insertion-work-line-perpendicular-base-line"
        value={props.value.base}
      />

      <LayoutPointPickerField
        label="Through Point"
        target="insertion-work-line-perpendicular-point"
        value={props.value.point}
      />
    </>
  );
}

function DivisionWorkLineControls(props: { value: DivisionInsertionWorkLine }) {
  const dispatch = useAppDispatch();

  return (
    <>
      <LayoutLinePickerField label="Line 1" target="insertion-work-line-division-line-1" value={props.value.base} />

      <LayoutLinePickerField label="Line 2" target="insertion-work-line-division-line-2" value={props.value.other} />

      <Tooltip disableInteractive title="Create multiple evenly-spaced work lines">
        <Stack>
          <NumericField
            integers
            max={256}
            label="Quantity"
            value={props.value.quantity}
            onSubmit={(value) => {
              dispatch(setWorkLineInsertionSettings({ divisionQuantity: value }));
            }}
          />
        </Stack>
      </Tooltip>
    </>
  );
}
